import * as PIXI from 'pixi.js';

import { AlphaAttribute, ColorAttribute } from '../attributes';
import type { Resolution, DestroyOptions } from '../../types';
import type { BaseAssetSettings } from './BaseAsset';
import { BaseAsset } from './BaseAsset';


/**
 * A ColorAsset Object will load the specified solid color.
 */
export class ColorAsset extends BaseAsset {
    override _classType = 'ColorAsset';

    // content
    _source: PIXI.Sprite = null; // PIXI sprite for external use
    #mediaTargetResolution: Resolution = undefined;

    @ColorAttribute()
    sourceColor = 0; // black
    @AlphaAttribute()
    sourceAlpha = 1.0; // fully opaque

    /**
     * @param {Luminary.Container} container - the container we want to house this asset under
     */
    constructor(container, settings: BaseAssetSettings) {
        super(container, settings);
        this.#mediaTargetResolution = Object.assign({}, this.root.referenceResolution);
    }

    /**
     * The resolution that best fits the media into the current slide.  This is usually either root resolution, or
     * the resolution of the resulting visible area when media is masked.
     */
    get mediaTargetResolution(): Resolution {
        return this.#mediaTargetResolution;
    }

    set mediaTargetResolution(resolution: Resolution) {
        this.#mediaTargetResolution.width = resolution.width;
        this.#mediaTargetResolution.height = resolution.height;

        if (this._source === null) {
            return;
        }

        this.applyMediaTargetResolution();
    }

    /** Apply Media Target Resolution - Description Needed
     */
    applyMediaTargetResolution() {
        if (!this.#mediaTargetResolution) {
            return;
        }
        this.width = this.#mediaTargetResolution.width;
        this.height = this.#mediaTargetResolution.height;
    }

    override _build() {
        this.clearAnimation();
        if (this._source) {
            this._source.destroy();
            this._source = null;
        }
        this._source = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._addInternalChild(this._source);

        // set target resolution
        this.applyMediaTargetResolution();

        this.applyAnimation();
        this.applyIntroAnimations();
        this.applyOutroAnimations();
    }

    override _draw() {
        if (!this._source) {
            return;
        }
        this._source.tint = this.sourceColor;
        this._source.alpha = this.sourceAlpha;
    }

    override destroy(options?: DestroyOptions) {
        super.destroy(options);
        this._source = null;
    }

    /**
     * `true` if the ColorAsset failed to load.
     */
    override get failedToLoad(): boolean {
        return false;
    }

    /**
     * @inheritDoc
     */
    override get isLoaded() {
        return true;
    }

    /**
     * This asset doesn't have to load anything remotely so it should "always load"
     * @inheritDoc
     */
    override get shouldLoad() {
        return true;
    }

    override applyTransformation() {
        // override in child class
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'mask',
            'sourceColor',
            'sourceAlpha',
        ]);
    }
}
